import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Trash2, Pencil, ShieldCheck } from "lucide-react"
import { adminUsersApi, type AdminUserDTO } from "@/lib/api"

type Props = {
  users: AdminUserDTO[]
  currentUserId: number
  onUsersChange: (u: AdminUserDTO[]) => void
}

function UserRow({ user, isSelf, onEdit, onDelete }: { user: AdminUserDTO; isSelf: boolean; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b last:border-0">
      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
        <ShieldCheck className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{user.email}</div>
        {isSelf && <div className="text-[10px] text-muted-foreground">You</div>}
      </div>
      <div className="flex items-center gap-1">
        <Button size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-foreground" onClick={onEdit}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        {!isSelf && (
          <Button size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-destructive" onClick={onDelete}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  )
}

export function AdminUsersPage({ users, currentUserId, onUsersChange }: Props) {
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<AdminUserDTO | null>(null)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const openCreate = () => { setEmail(""); setPassword(""); setErr(null); setCreateOpen(true) }
  const openEdit = (u: AdminUserDTO) => { setEditTarget(u); setEmail(u.email); setPassword(""); setErr(null) }
  const closeEdit = () => { setEditTarget(null); setEmail(""); setPassword("") }

  const handleCreate = async () => {
    if (!email || !password) { setErr("Email and password are required."); return }
    setLoading(true); setErr(null)
    try {
      const dto = await adminUsersApi.create({ email, password })
      onUsersChange([...users, dto])
      setCreateOpen(false)
    } catch {
      setErr("Failed to create user. Email may already be in use.")
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async () => {
    if (!editTarget) return
    const body: { email?: string; password?: string } = {}
    if (email && email !== editTarget.email) body.email = email
    if (password) body.password = password
    if (!Object.keys(body).length) { closeEdit(); return }
    setLoading(true); setErr(null)
    try {
      const dto = await adminUsersApi.update(editTarget.id, body)
      onUsersChange(users.map(u => u.id === dto.id ? dto : u))
      closeEdit()
    } catch {
      setErr("Failed to update user.")
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    await adminUsersApi.remove(id).catch(() => {})
    onUsersChange(users.filter(u => u.id !== id))
  }

  return (
    <div className="flex flex-col gap-5 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Admin Users</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Manage accounts with full admin access</p>
        </div>
        <Button size="sm" className="rounded-full" onClick={openCreate}>+ New admin</Button>
      </div>

      <Card className="overflow-hidden p-0">
        {users.length === 0 && (
          <div className="py-10 text-center text-sm text-muted-foreground">No admin users yet.</div>
        )}
        {users.map(u => (
          <UserRow key={u.id} user={u} isSelf={u.id === currentUserId} onEdit={() => openEdit(u)} onDelete={() => handleDelete(u.id)} />
        ))}
      </Card>

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={v => { setCreateOpen(v); if (!v) setErr(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New admin user</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3 pt-1">
            <Input autoFocus placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} />
            <Input placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()} />
            {err && <span className="text-xs text-destructive">{err}</span>}
            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleCreate} disabled={loading}>Create</Button>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={!!editTarget} onOpenChange={v => { if (!v) closeEdit(); setErr(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit admin user</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3 pt-1">
            <Input placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} />
            <Input placeholder="New password (leave blank to keep)" type="password" value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleUpdate()} />
            {err && <span className="text-xs text-destructive">{err}</span>}
            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleUpdate} disabled={loading}>Save</Button>
              <Button variant="outline" onClick={closeEdit}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
